import {
  CLEANING_FEE,
  findFeeByDescription,
  getDiscountValue,
  GUEST_FEE,
  INCOMPLETE,
  NOT_STARTED,
  OccupancyTax,
  OTHER_FEE,
  PET_FEE,
} from "pages/listing-details-with-step/src/common"
import { PDPType, PropertyDataNew, RboPropertyDataTypes } from "services/common"
import { UpdateLocationAddressType } from "services/rbo/location-details-api"
import {
  AvailabilityType,
  GetOnboardingStatusData,
  PricingType,
} from "services/rbo/onboarding-status-data"
import { PolicyType } from "services/rbo/review-and-publish"

/**
 * Checks if the location data has been updated by comparing the stored location
 * from onboarding data with the current address.
 *
 * @returns {boolean} - Returns `true` if the location has changed or if no stored location exists.
 */

export const isLocationUpdated = (
  onBoardingStatusData: GetOnboardingStatusData | undefined,
  currentAddress: UpdateLocationAddressType | null,
  propertyData: PropertyDataNew | undefined,
) => {
  try {
    const data = propertyData?.propData
    const locationData = propertyData?.propertyLocation

    const storedLocation: UpdateLocationAddressType =
      typeof onBoardingStatusData?.location === "string"
        ? JSON.parse(onBoardingStatusData?.location)
        : null
    const status = onBoardingStatusData?.locationStatus

    if (status === null || status === NOT_STARTED) {
      return true
    }
    // If status is INCOMPLETE, check if stored location differs from the current address
    if (status === INCOMPLETE) {
      return !(
        storedLocation?.area === currentAddress?.area &&
        storedLocation?.city === currentAddress?.city &&
        storedLocation?.zipcode === currentAddress?.zipcode &&
        storedLocation?.street === currentAddress?.street &&
        storedLocation?.stateCode === currentAddress?.stateCode
      )
    }
    return !(
      data?.addressline1 === currentAddress?.street &&
      data?.addressline2 === currentAddress?.area &&
      (locationData?.city || data?.city || "") === currentAddress?.city &&
      data?.postal_code === currentAddress?.zipcode &&
      (locationData?.state || data?.state || "") === currentAddress?.stateCode
    )
  } catch (error) {
    console.error("Error parsing location data:", error)
    return true
  }
}

/**
 * Checks if the pricing details have been updated by comparing stored data with the current state.
 * @returns {boolean} - Returns `true` if there is a change in pricing data, otherwise `false`.
 */
export const isPricingUpdated = (
  onBoardingStatusData: GetOnboardingStatusData | undefined,
  currentPricingValue: PricingType | null,
  pricingData: PropertyDataNew | undefined,
) => {
  try {
    // Ensure stored pricing data is always parsed correctly
    const storedPricing: PricingType =
      typeof onBoardingStatusData?.pricing === "string"
        ? JSON.parse(onBoardingStatusData?.pricing)
        : null
    const status = onBoardingStatusData?.pricingStatus // Retrieve the current pricing status from onboarding data
    // If the pricing setup is either NOT_STARTED or INCOMPLETE, compare stored and current values
    if (status === null || status === NOT_STARTED) {
      return true
    }
    if (status === INCOMPLETE) {
      // Compare stored pricing values with the current state values
      return !(
        storedPricing?.basePrice === currentPricingValue?.basePrice &&
        storedPricing?.discount === currentPricingValue?.discount &&
        storedPricing?.extendedDiscount ===
          currentPricingValue?.extendedDiscount &&
        storedPricing?.cleaningFee === currentPricingValue?.cleaningFee &&
        storedPricing?.petFee === currentPricingValue?.petFee &&
        storedPricing?.petCount === currentPricingValue?.petCount &&
        storedPricing?.petFeeType === currentPricingValue?.petFeeType &&
        storedPricing?.tax === currentPricingValue?.tax &&
        // Check if the 'I do not have any taxes' option is present in the stored data and matches the current value
        storedPricing?.isTaxApplied === currentPricingValue?.isTaxApplied &&
        storedPricing?.additionalGuestFee ===
          currentPricingValue?.additionalGuestFee &&
        storedPricing?.additionalGuestCount ===
          currentPricingValue?.additionalGuestCount &&
        storedPricing?.otherFeeValue === currentPricingValue?.otherFeeValue &&
        storedPricing?.otherFeeType === currentPricingValue?.otherFeeType
      )
    }

    // Extract fee values from pricing data
    const cleaningFee = findFeeByDescription(
      pricingData?.rateInfo?.feeInfo || [],
      CLEANING_FEE,
    )
    const petFee = findFeeByDescription(
      pricingData?.rateInfo?.feeInfo || [],
      PET_FEE,
    )
    const occupancyTax = findFeeByDescription(
      pricingData?.rateInfo?.taxInfo || [],
      OccupancyTax,
    )

    const additionalGuestFee = findFeeByDescription(
      pricingData?.rateInfo?.feeInfo || [],
      GUEST_FEE,
    )

    const otherFeeValue = findFeeByDescription(
      pricingData?.rateInfo?.feeInfo || [],
      OTHER_FEE,
    )

    return !(
      (pricingData?.rboPropertyData?.base_rate || 0) ===
        currentPricingValue?.basePrice &&
      (getDiscountValue(pricingData?.discountInfoNew || [], "0") ?? 20) ===
        currentPricingValue?.discount &&
      getDiscountValue(pricingData?.discountInfoNew || [], "1") ===
        currentPricingValue?.extendedDiscount &&
      Number(cleaningFee?.value || 0) === currentPricingValue?.cleaningFee &&
      (pricingData?.propData?.suitable_pet
        ? Number(petFee?.value || 0) === currentPricingValue?.petFee // If pets are allowed, compare pet fees
        : true) && // Otherwise, ignore pet fee comparison
      (pricingData?.propData?.suitable_pet
        ? Number(pricingData?.propData?.max_pet_count || 1) ===
          currentPricingValue?.petCount // If pets are allowed, compare pet counts
        : true) &&
      (pricingData?.propData?.suitable_pet
        ? petFee?.type === currentPricingValue?.petFeeType // If pets are allowed, compare pet fees
        : true) &&
      Number(additionalGuestFee?.value || 0) ===
        currentPricingValue?.additionalGuestFee &&
      Number(pricingData?.propData?.minimumNonChargeableAdult || 1) ===
        currentPricingValue?.additionalGuestCount &&
      Number(otherFeeValue?.value || 0) ===
        currentPricingValue?.otherFeeValue &&
      otherFeeValue?.type === currentPricingValue?.otherFeeType &&
      Number(occupancyTax?.value || 0) === currentPricingValue?.tax &&
      // Check if the 'I do not have any taxes' option is present in the stored data and matches the current value
      storedPricing?.isTaxApplied === currentPricingValue?.isTaxApplied
    )
  } catch (error) {
    console.error("Error parsing pricing data:", error)
    return true // Assume pricing has changed if there's an error in parsing
  }
}

/**
 * Checks if the availability data has been updated compared to the stored onboarding status data.
 *
 * @param {GetOnboardingStatusData | undefined} onBoardingStatusData - The onboarding status data that may contain stored availability details.
 * @param {AvailabilityType | null} currentAvailabilityValue - The current availability settings.
 * @param {RboPropertyDataTypes} pricingData - The pricing data containing minimum and maximum length of stay.
 * @returns {boolean} - Returns `true` if the availability data has been updated, otherwise `false`.
 */
export const isAvailabilityUpdated = (
  onBoardingStatusData: GetOnboardingStatusData | undefined,
  currentAvailabilityValue: AvailabilityType | null,
  pricingData: RboPropertyDataTypes | undefined,
): boolean => {
  // Parse stored availability data safely (handle string or object formats)
  const storedAvailability: AvailabilityType =
    typeof onBoardingStatusData?.availability === "string"
      ? JSON.parse(onBoardingStatusData.availability)
      : onBoardingStatusData?.availability

  const status = onBoardingStatusData?.availabilityStatus

  // If availability status is missing or not started, consider it as updated
  if (status === null || status === NOT_STARTED) {
    return true
  }

  // If status is INCOMPLETE, check if stored availability differs from the current value
  if (status === INCOMPLETE) {
    return !(
      storedAvailability?.minLengthStay ===
        currentAvailabilityValue?.minLengthStay &&
      storedAvailability?.maxLengthStay ===
        currentAvailabilityValue?.maxLengthStay
    )
  }

  // Otherwise, compare the property availability data with the current availability settings
  return !(
    pricingData?.minimum_los === currentAvailabilityValue?.minLengthStay &&
    pricingData?.maximum_los === currentAvailabilityValue?.maxLengthStay
  )
}

/**
 * Checks if the Property Damage Protection (PDP) plan has changed.
 * @returns {boolean} - Returns `true` if the PDP plan has changed, otherwise `false`.
 */
export const isPdpPlanUpdated = (
  onBoardingStatusData: GetOnboardingStatusData | undefined,
  currentPdpPlan: string | null,
  pdpPlan: string,
) => {
  // Parse stored PDP plan data safely
  const storedPdpPlan: Record<string, any> =
    typeof onBoardingStatusData?.propertyDamage === "string"
      ? JSON.parse(onBoardingStatusData?.propertyDamage)
      : onBoardingStatusData?.propertyDamage
  const status = onBoardingStatusData?.propertyDamageStatus
  /**
   * Determines the PDP plan type based on the stored data.
   * @returns {string} - Returns "Existing" if it's a user-provided plan,
   * "Whimstay" if it's a Whimstay-provided plan, or an empty string if undefined.
   */
  const getPdpPlan = () => {
    if (
      storedPdpPlan?.isMyOwnPlan === "true" ||
      storedPdpPlan?.isMyOwnPlan === true
    ) {
      return PDPType?.Existing // I will use my own property damage protection plan
    }

    if (
      storedPdpPlan?.isWhimstayPlan === "true" ||
      storedPdpPlan?.isWhimstayPlan === true
    ) {
      return PDPType?.Whimstay // I will use the Whimstay property damage protection plan
    }

    return "" // No plan selected
  }
  if (status === null || status === NOT_STARTED) {
    return true
  }
  // If status is INCOMPLETE, check if stored PDP plan differs from the current value
  if (status === INCOMPLETE) {
    // Compare stored PDP plan with the current PDP plan value
    return getPdpPlan() !== currentPdpPlan
  }
  return pdpPlan !== currentPdpPlan // Compare current PDP plan with the stored value
}

/**
 * Checks if the cancellation policy has changed.
 * @returns {boolean} - Returns `true` if the policy has changed, otherwise `false`.
 */
export const isCancellationPolicyUpdated = (
  onBoardingStatusData: GetOnboardingStatusData | undefined,
  currentCancellationPolicy: PolicyType | null,
  cancellationPolicy: PolicyType | undefined,
) => {
  // Parse stored cancellation policy safely, ensuring it's an object
  const storedCancellationPolicy: Record<string, boolean> =
    typeof onBoardingStatusData?.cancellationPolicy === "string"
      ? JSON.parse(onBoardingStatusData.cancellationPolicy)
      : onBoardingStatusData?.cancellationPolicy || {}
  const status = onBoardingStatusData?.cancellationStatus // Retrieve the current cancellation status from onboarding data
  if (status === null || status === NOT_STARTED) {
    return true
  }
  if (status === INCOMPLETE) {
    // If either stored policy or current policy ID is missing, assume a change
    if (!storedCancellationPolicy || !currentCancellationPolicy?.id) {
      return true
    }
    // Check if the current cancellation policy ID exists in stored policies
    return !storedCancellationPolicy[currentCancellationPolicy.id] || false
  }
  return cancellationPolicy?.id !== currentCancellationPolicy?.id || false
}

/**
 * Checks if the minimum age requirement has changed.
 * @returns {boolean} - Returns `true` if the minimum age requirement has changed, otherwise `false`.
 */
export const isMinimumAgeUpdated = (
  onBoardingStatusData: GetOnboardingStatusData | undefined,
  currentMinimumAgeValue: string | null,
  minAge: string,
) => {
  // Retrieve the stored minimum age requirement, defaulting to an empty string if not found
  const storedMinimumAgeRequirement =
    onBoardingStatusData?.minAgeRequirement || ""
  const status = onBoardingStatusData?.minAgeStatus // Retrieve the current minimum age status from onboarding data
  if (status === null || status === NOT_STARTED) {
    return true
  }
  if (status === INCOMPLETE) {
    // Compare the stored value with the current minimum age requirement
    return storedMinimumAgeRequirement !== currentMinimumAgeValue
  }
  return minAge !== currentMinimumAgeValue
}

/**
 * Checks if the imported calendar link has been updated.
 *
 * This function compares the stored calendar link from `onBoardingStatusData`
 * with the current calendar link to determine if an update is needed.
 *
 * @returns {boolean} - Returns `true` if the import calendar link has changed, otherwise `false`.
 * @param onBoardingStatusData
 * @param currentImportCalendarLink
 * @param calendarLink
 */
export const isUpdatedImportCalendarLink = (
  onBoardingStatusData: GetOnboardingStatusData | undefined,
  currentImportCalendarLink: string,
  calendarLink: string,
) => {
  try {
    // Parse the stored calendar data if it's a string, otherwise use the object directly
    const storedImportCalendarLink: Record<string, any> =
      typeof onBoardingStatusData?.calendar === "string"
        ? JSON.parse(onBoardingStatusData.calendar)
        : onBoardingStatusData?.calendar || {}
    const status = onBoardingStatusData?.calendarStatus
    if (status === null || status === NOT_STARTED) {
      return true
    }
    if (status === INCOMPLETE) {
      // Compare stored import calendar link with the current value
      return (
        storedImportCalendarLink?.importCalendar !== currentImportCalendarLink
      )
    }
    return calendarLink !== currentImportCalendarLink // Compare current calendar link with the stored value
  } catch (error) {
    console.error("Error parsing calendar data:", error)
    return true // Assume an update is needed in case of an error
  }
}
